// @flow
import { createSelector } from "reselect";
import { castArray, isEmpty, isString } from "lodash";
import consts from "common/consts";
import { createDeepEqualSelector } from "utils/reselectUtils";
import { formatFileType } from "~/src/uploader/common/utils";

const getWidgetConfig = (state: Object) => state.widgetConfig;

const getUploadQueue = (state: Object) => state.uploadQueue;

const getWidgetData = (state: Object) => state.widgetData;

const getWidgetStyles = (state: Object) => state.widgetStyles;

const getQueueCounters = (uploadQueue: Object) => {
	const { queueItems } = uploadQueue;
	const { queueIds } = uploadQueue;
	const result = queueIds.reduce((res, id) => {
		const item = queueItems[id];
		res.active += (item.progress < 100 ? 1 : 0);
		res.processing += (!item.done && !item.failed && item.progress === 100 ? 1 : 0);
		res.failed += (item.failed ? 1 : 0);
		return res;
	}, {
		active: 0, processing: 0, failed: 0, done: 0, total: queueIds.length,
	});

	const done = result.total - (result.active + result.failed + result.processing);
	const hasActive = !!(result.active || uploadQueue.simulatedQueue);
	const hasProcessing = result.processing > 0;

	return {
		...result,
		done,
		hasActive,
		hasProcessing,
		isSimulated: uploadQueue.simulatedQueue,
		isDone: !hasActive && !hasProcessing
			&& (done > 0 || (!!result.failed && result.failed === result.total)),
	};
};

const selectMenuData: Object => Object = createDeepEqualSelector(
	(state) => ({
		sources: state.widgetConfig.sources,
		source: state.widgetData.source,
		responsiveMenuShow: state.widgetData.responsiveMenuShow,
		isResponsiveWidth: state.widgetData.isResponsiveWidth,
	}),
	(data) => ({
		enabledSources: data.sources,
		isSingle: data.sources.length === 1,
		currentSource: data.source,
		responsiveShow: data.sources.length > 1 && data.responsiveMenuShow,
		isResponsiveWidth: data.isResponsiveWidth,
	}),
);

const selectIsMultipleEnabled: Object => boolean = createSelector(
	getWidgetConfig,
	(widgetConfig) => widgetConfig.multiple,
);

const selectLocation: Object => Object = createSelector(
	(state: Object) => state.router.location,
	(location) => location,
);

const selectNotification: Object => Object = createSelector(
	getWidgetData,
	(data) => {
		const id = Object.keys(data.notifications).find((id) => data.notifications[id]);
		return id ? data.notifications[id] : {};
	},
);

const selectIsResponsiveWidth: Object => boolean = createSelector(
	getWidgetData,
	(data) => data.isResponsiveWidth,
);

const selectDefaultSource: Object => Object = createSelector(
	getWidgetConfig,
	(data) => ({ defaultSource: data.defaultSource }),
);

const selectIsAdvancedOptionsOpen: Object => boolean = createSelector(
	getWidgetData,
	(data) => data.advancedOptionsOpen,
);

const selectShouldShowAdvancedOptions: Object => boolean = createSelector(
	getWidgetConfig,
	(config) => config.showAdvancedOptions,
);

const selectClientAllowedFormatsOptions: Object => string = createSelector(
	getWidgetConfig,
	(config) => {
		const clientAllowedArray = isString(config.clientAllowedFormats) ?
			config.clientAllowedFormats.split(",") :
			config.clientAllowedFormats;

		return isEmpty(clientAllowedArray) ? null : castArray(clientAllowedArray).map(formatFileType).join(",");
	},
);

const selectAdvancedOptions: Object => Object = createSelector(
	getWidgetConfig,
	getWidgetData,
	(config, data) => ({
		useTagsCallback: config.useTagsCallback,
		useUploadPresetsCallback: config.useUploadPresetsCallback,
		setPublicId: data.advancedPublicId,
		setTags: data.advancedTags,
		setUploadPreset: data.advancedUploadPreset,
		suggestedTags: data.suggestedTags,
		suggestedUploadPresets: data.suggestedUploadPresets,
		tagsInputValue: data.tagsInputValue,
		publicIdError: data.publicIdError,
	}),
);

const selectCloudName: Object => string = createSelector(
	getWidgetConfig,
	(config) => config.cloudName,
);

const selectHasActiveUploads: Object => boolean = createSelector(
	getUploadQueue,
	(queueState) => getQueueCounters(queueState).active > 0,
);

const selectCloseCounter: Object => number = (state: Object) => state.widgetData.widgetCloseCounter;

const selectUploadContainerData: Object => Object = createDeepEqualSelector(
	selectCloseCounter,
	(state) => ({
		added: state.uploadQueue.added,
		queueIds: state.uploadQueue.queueIds,
		widgetViewType: state.widgetData.widgetViewType,
		advancedPublicId: state.widgetData.advancedPublicId,
		advancedTags: state.widgetData.advancedTags,
		advancedUploadPreset: state.widgetData.advancedUploadPreset,
		tagsInputValue: state.widgetData.tagsInputValue,
		metadataInfo: state.widgetData.metadataInfo,
	}),
	(closeCounter, data) => ({
		uploadAdded: data.added,
		queueIds: data.queueIds,
		closeCounter,
		widgetViewType: data.widgetViewType,
		metadataInfo: data.metadataInfo,
		uploadOptions: {
			publicId: data.advancedPublicId,
			tags: data.advancedTags,
			tagsInputValue: data.tagsInputValue,
			uploadPreset: data.advancedUploadPreset,
		},
	}),
);

const selectRetryIds = createSelector(
	getUploadQueue,
	(queue) => queue.retryIds
);

const selectAbortIds = createSelector(
	getUploadQueue,
	(queue) => queue.abortIds
);

const selectAddedUploads: Object => Object = createSelector(
	getUploadQueue,
	(queue) => queue.added,
);

// only show powered by if premium info received - to make sure it doesnt appear and then disappear if customer wants allowed to remove
const selectShowPoweredBy: Object => boolean = createSelector(
	getWidgetConfig,
	getWidgetData,
	(config, data) => data.premiumInfoReceived
		&& ((config.showPoweredBy !== false && data.isPremium) || !data.isPremium),
);

const selectBatchStartCounter: Object => number = createSelector(
	getWidgetData,
	(data) => data.batchStartCounter,
);

const selectWidgetViewType: Object => number = createSelector(
	getWidgetData,
	(data) => data.widgetViewType,
);

const selectIsInlineMode: Object => boolean = createSelector(
	getWidgetConfig,
	(config) => config.inlineMode,
);

const selectWidgetViewTypeChangeByUser: Object => boolean = createSelector(
	getWidgetData,
	(data) => data.widgetViewTypeChangeByUser,
);

const selectAutoMinimizeConfig: Object => boolean = createSelector(
	getWidgetConfig,
	(config) => config.autoMinimize,
);

const selectConfigLanguage: Object => string = createSelector(
	getWidgetConfig,
	(config) => config.language,
);

const selectDictionaryTextVersion: Object => number = createSelector(
	getWidgetData,
	(data) => data.dictionaryTextVersion,
);

const selectCustomImagesResults: Object => Object = createSelector(
	getWidgetStyles,
	(styles) => styles.customImagesResults,
);

const selectCustomImagesToLoad: Object => Object = createSelector(
	getWidgetStyles,
	(styles) => styles.loadCustomImages,
);

const selectPalette: Object => Object = createSelector(
	getWidgetStyles,
	(styles) => styles.palette,
);

const selectFonts: Object => Object = createSelector(
	getWidgetStyles,
	(styles) => styles.fonts,
);

const selectIsThinMenu: Object => boolean = createSelector(
	getWidgetConfig,
	(config) => config.sources.length === 1
);

const selectPreQueuePath: Object => string = createSelector(
	getWidgetData,
	(data) => data.preQueuePath,
);

const selectFrameBackground = createSelector(
	getWidgetStyles,
	(styles) => styles.frame.background
);

const selectSingleUploadAutoClose: Object => boolean = createSelector(
	getWidgetConfig,
	(config) => config.singleUploadAutoClose,
);

const selectIsShowing = (state: Object) => state.widgetData.isShowing;

const selectMaxImageFileSize: Object => number = createSelector(
	getWidgetConfig,
	(config) => config.maxImageFileSize,
);

const selectMaxVideoFileSize: Object => number = createSelector(
	getWidgetConfig,
	(config) => config.maxVideoFileSize,
);

const selectMaxFileSize: Object => number = createSelector(
	getWidgetConfig,
	(config) => config.maxFileSize,
);

const selectTabInsideWidget: Object => number = createSelector(
	getWidgetConfig,
	(config) => config.tabInsideWidget
);

const selectTopMenuConfigTitles: Object => string = createSelector(
	getWidgetConfig,
	selectConfigLanguage,
	(config, lang) => {
		const texts = config.text && config.text[lang];
		return texts && texts.menu
			&& Object.values(texts.menu)
				.join("");
	},
);

const selectIsInCrop: Object => boolean = createSelector(
	selectLocation,
	(location) => !!~location.pathname.indexOf(consts.CROP),
);

const selectIsCropEnabled: Object => boolean = createSelector(
	getWidgetConfig,
	(config) => config.cropping,
);

const selectIsInQueue = createSelector(
	selectLocation,
	(location) => !!~location.pathname.indexOf(consts.QUEUE)
);

const selectNewTlsDomain: Object => boolean = createSelector(
	getWidgetConfig,
	(config) => !!config.newTlsDomain
);

const selectTabRoutes: Object => string[] = createSelector(
	getWidgetData,
	(data) => data.tabRoutes,
);

export {
	getQueueCounters,
	getWidgetConfig,
	selectMenuData,
	selectIsMultipleEnabled,
	selectLocation,
	selectNotification,
	selectDefaultSource,
	selectAdvancedOptions,
	selectIsAdvancedOptionsOpen,
	selectCloudName,
	selectUploadContainerData,
	selectIsResponsiveWidth,
	selectShowPoweredBy,
	selectHasActiveUploads,
	selectBatchStartCounter,
	selectWidgetViewType,
	selectAutoMinimizeConfig,
	selectWidgetViewTypeChangeByUser,
	selectConfigLanguage,
	selectShouldShowAdvancedOptions,
	selectCustomImagesResults,
	selectCustomImagesToLoad,
	selectPalette,
	selectFonts,
	selectDictionaryTextVersion,
	selectIsThinMenu,
	selectAddedUploads,
	selectPreQueuePath,
	selectIsInlineMode,
	selectFrameBackground,
	selectSingleUploadAutoClose,
	selectCloseCounter,
	selectIsShowing,
	selectMaxImageFileSize,
	selectMaxVideoFileSize,
	selectMaxFileSize,
	selectTopMenuConfigTitles,
	selectIsCropEnabled,
	selectIsInCrop,
	selectAbortIds,
	selectRetryIds,
	selectIsInQueue,
	selectClientAllowedFormatsOptions,
	selectTabInsideWidget,
	selectNewTlsDomain,
	selectTabRoutes,
};
